// @ts-nocheck

import { createSelector } from 'reselect';

import {
  selectBasketIsCashout,
  selectBasketItems,
  selectBasketMode,
  selectBasketState,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketShowResume } from '../../../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems } from '../../../../selectors/basketItems.selector';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { BasketModes, BasketStatuses } from '../../../../types/basket';
import { basketSetIsMPActive, basketItemSetIsMPActive } from '../../actions/basket';
import { MPGlobalStatuses } from '../../types/global';
import { MPUserStatuses } from '../../types/user';
import { selectMPGlobalStatus } from '../global';
import { selectMPUserStatus } from '../user';

import { selectIsMPEnabled } from './runtime';

const getOddsIdFromProps = (_, { oddsId }) => oddsId ?? null;
const getDispatchFromProps = (_, { dispatch }) => dispatch;

const selectMPBasketSwitchIsDisabled = createSelector(
  selectBasketStatus,
  (basketStatus) => basketStatus === BasketStatuses.PENDING,
);

export const selectAllMPBasketCards = createSelector([selectBasketState, selectBasketItems], (basket, items) => {
  return Object.keys(items).reduce((result, element) => {
    if (items[element].monopolyCard) {
      result.push(items[element].monopolyCard);
    }

    return result;
  }, []);
});

export const selectMPBasketCard = createSelector(
  [selectBasketMode, getOddsIdFromProps, selectBasketItems, selectBasketState],
  (basketMode, oddsId, basketItems, basket) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isModeCombined = basketMode === BasketModes.COMBINED;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

    if (isOneSimpleBet) {
      const foundOddsId = Object.keys(basketItems)[0];
      return basketItems[foundOddsId]?.monopolyCard;
    }
    if (isModeSimple) {
      return basketItems[oddsId]?.monopolyCard;
    }
    if (isModeCombined) {
      return basket.monopolyCard;
    }
  },
);

export const selectMPBasketCardFamilyId = createSelector([selectMPBasketCard], (card) => card?.familyId);

export const selectMPBasketCardIndex = createSelector([selectMPBasketCard], (card) => card?.index);

const selectMPBasketSwitchIsVisible = createSelector(
  [
    selectUserConnected,
    selectMPUserStatus,
    selectBasketShowResume,
    selectIsMPEnabled,
    selectMPGlobalStatus,
    selectBasketMode,
    selectBasketIsCashout,
    selectBasketEnhancedItems,
    getOddsIdFromProps,
    selectMPBasketCard,
  ],
  (userIsConnected, userStatus, showResume, isMPEnabled, MPStatus, basketMode, isCashout, items, oddsId, card) => {
    if (isMPEnabled && MPStatus === MPGlobalStatuses.RUNNING && card) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isModeCombined = basketMode === BasketModes.COMBINED;
      const isModeSystem = basketMode === BasketModes.SYSTEM;

      const isOneSimpleBet = isModeSimple && items.length === 1;
      const isMultipleSimpleBets = isModeSimple && items.length > 1;

      const isEligible = !(
        isModeSystem ||
        // eslint-disable-next-line sonarjs/no-gratuitous-expressions
        (isModeCombined && !isMPEnabled) ||
        // eslint-disable-next-line sonarjs/no-gratuitous-expressions
        (isModeSimple && ((isMultipleSimpleBets && !isMPEnabled) || (isOneSimpleBet && !isMPEnabled))) ||
        isCashout
      );

      return (
        userIsConnected &&
        userStatus === MPUserStatuses.IN &&
        isEligible &&
        !showResume &&
        ((isOneSimpleBet && !oddsId) || (!isModeSimple && !oddsId) || (isMultipleSimpleBets && oddsId))
      );
    }

    return false;
  },
);

const selectMPBasketSwitchIsToggled = createSelector(
  [selectBasketMode, getOddsIdFromProps, selectBasketItems, selectBasketState],
  (basketMode, oddsId, basketItems, basket) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isModeCombined = basketMode === BasketModes.COMBINED;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

    if (isOneSimpleBet) {
      const foundOddsId = Object.keys(basketItems)[0];
      return basketItems[foundOddsId]?.isMonopolyActive;
    }
    if (isModeSimple) {
      return basketItems[oddsId]?.isMonopolyActive;
    }
    if (isModeCombined) {
      return basket.isMonopolyActive;
    }
  },
);

const selectMPBasketSwitchOnToggle = createSelector(
  [getDispatchFromProps, getOddsIdFromProps, selectBasketMode, selectBasketItems, selectMPBasketCard],
  (dispatch, oddsId, basketMode, basketItems, card) => (isSelected) => {
    if (card) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

      if (basketMode === BasketModes.COMBINED) {
        dispatch(
          basketSetIsMPActive({
            isMonopolyActive: isSelected ? isSelected : false,
          }),
        );
      } else if (oddsId) {
        dispatch(
          basketItemSetIsMPActive({
            oddsId,
            isMonopolyActive: isSelected ? isSelected : false,
          }),
        );
      } else {
        if (isOneSimpleBet) {
          const foundOddsId = Object.keys(basketItems)[0];

          if (foundOddsId) {
            dispatch(
              basketItemSetIsMPActive({
                oddsId: foundOddsId,
                isMonopolyActive: isSelected ? isSelected : false,
              }),
            );
          }
        }
      }
    }
  },
);

export const selectMPBasketSwitchProps = createSelector(
  [
    selectMPBasketSwitchIsVisible,
    selectMPBasketSwitchIsDisabled,
    selectMPBasketSwitchIsToggled,
    selectMPBasketSwitchOnToggle,
    selectMPBasketCard,
  ],
  (isVisible, disabled, toggled, onToggle, card) => ({
    isVisible,
    disabled,
    toggled,
    onToggle,
    card,
  }),
);
